import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, Search, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
    handled: { dot: "bg-[#56CCF2]", text: "text-[#56CCF2]", label: "HANDLED" },
    verified: { dot: "bg-[#56CCF2]", text: "text-[#56CCF2]", label: "VERIFIED" },
    received: { dot: "bg-[#94A3B8]", text: "text-[#94A3B8]", label: "RECEIVED" },
    error: { dot: "bg-[#FB7185]", text: "text-[#FB7185]", label: "ERROR" },
    rejected: { dot: "bg-[#F4B731]", text: "text-[#F4B731]", label: "REJECTED" },
};

const TOPIC_OPTIONS = [
    "", "customers/update", "orders/paid",
    "subscription_contracts/activate", "subscription_contracts/cancel",
    "subscription_contracts/pause", "subscription_contracts/expire",
    "subscription_contracts/fail",
];

function StatusPill({ status }) {
    const s = STATUS_STYLES[status] || STATUS_STYLES.received;
    return (
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-medium">
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            <span className={s.text}>{s.label}</span>
        </span>
    );
}

function formatTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

export default function AdminWebhooks() {
    const [events, setEvents] = useState([]);
    const [summary, setSummary] = useState({ total_24h: 0, failed_24h: 0, last_event: null });
    const [loading, setLoading] = useState(true);
    const [topic, setTopic] = useState("");
    const [status, setStatus] = useState("");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        const params = {};
        if (topic) params.topic = topic;
        if (status) params.status = status;
        if (search && search.includes("@")) params.email = search.trim().toLowerCase();
        else if (search) params.customer_id = search.trim();
        api.get("/admin/webhooks", { params })
            .then((r) => {
                setEvents(r.data.events || []);
                setSummary(r.data.summary || { total_24h: 0, failed_24h: 0, last_event: null });
            })
            .catch(() => toast.error("Failed to load webhook events"))
            .finally(() => setLoading(false));
    }, [topic, status, search]);

    useEffect(() => { load(); }, [load]);

    const openEvent = (ev) => {
        api.get(`/admin/webhooks/${ev.id}`)
            .then((r) => setSelected(r.data))
            .catch(() => toast.error("Failed to load event detail"));
    };

    return (
        <AdminLayout>
            <div className="p-8 sm:p-10 max-w-6xl">
                <header className="mb-8">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] mb-3">
                        <Activity className="w-3.5 h-3.5" />
                        Operations · Shopify
                    </div>
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-white tracking-tight">Webhook event log</h1>
                            <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl leading-relaxed">
                                Every Shopify webhook is persisted here for 90 days: receipt, HMAC verification, dispatch result, and the action taken on the member record. Filter by topic, status, customer or email.
                            </p>
                        </div>
                        <button
                            data-testid="webhooks-refresh"
                            onClick={load}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs uppercase tracking-[0.18em] text-[#94A3B8] hover:text-white border border-[#243044] hover:border-[#2F80ED] transition-colors"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </button>
                    </div>
                </header>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-[#111827] border border-[#243044] rounded-xl p-5">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748B] mb-3">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            Total 24h
                        </div>
                        <div className="text-3xl font-bold text-white tracking-tight" data-testid="wh-total-24h">{summary.total_24h}</div>
                    </div>
                    <div className="bg-[#111827] border border-[#243044] rounded-xl p-5">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748B] mb-3">
                            <ShieldAlert className="w-3.5 h-3.5 text-[#FB7185]" />
                            Failed 24h
                        </div>
                        <div className={`text-3xl font-bold tracking-tight ${summary.failed_24h > 0 ? "text-[#FB7185]" : "text-white"}`} data-testid="wh-failed-24h">
                            {summary.failed_24h}
                        </div>
                    </div>
                    <div className="bg-[#111827] border border-[#243044] rounded-xl p-5">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748B] mb-3">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Last event
                        </div>
                        <div className="text-sm text-white" data-testid="wh-last-event">
                            {summary.last_event ? (
                                <>
                                    <div className="font-medium">{summary.last_event.topic}</div>
                                    <div className="text-xs text-[#64748B] mt-0.5">{formatTime(summary.last_event.received_at)} · <StatusPill status={summary.last_event.status} /></div>
                                </>
                            ) : (
                                <span className="text-[#64748B] italic">No webhooks received yet</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div className="flex items-center gap-2 bg-[#111827] border border-[#243044] rounded-xl px-3 py-2 focus-within:border-[#2F80ED]">
                        <Search className="w-3.5 h-3.5 text-[#64748B]" />
                        <input
                            data-testid="wh-search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            placeholder="Search email or customer id…"
                            className="bg-transparent border-0 outline-none text-xs text-white placeholder-[#64748B] w-64"
                        />
                    </div>
                    <select
                        data-testid="wh-filter-topic"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        className="bg-[#111827] border border-[#243044] text-xs text-white rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#2F80ED]"
                    >
                        {TOPIC_OPTIONS.map((t) => (
                            <option key={t || "all"} value={t}>{t || "All topics"}</option>
                        ))}
                    </select>
                    <select
                        data-testid="wh-filter-status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="bg-[#111827] border border-[#243044] text-xs text-white rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#2F80ED]"
                    >
                        <option value="">All statuses</option>
                        <option value="handled">Handled</option>
                        <option value="rejected">Rejected</option>
                        <option value="error">Error</option>
                        <option value="verified">Verified</option>
                        <option value="received">Received</option>
                    </select>
                </div>

                <div className="bg-[#111827] border border-[#243044] rounded-xl overflow-hidden">
                    <div className="px-6 py-3 border-b border-[#243044] flex items-center justify-between">
                        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-[#94A3B8]">Events</h2>
                        <span className="text-xs text-[#64748B]">{events.length} shown · TTL 90 days</span>
                    </div>
                    {loading ? (
                        <div className="px-6 py-10 text-center text-sm text-[#64748B]">Loading events…</div>
                    ) : events.length === 0 ? (
                        <div className="px-6 py-14 text-center">
                            <AlertTriangle className="w-5 h-5 text-[#64748B] mx-auto mb-3" />
                            <p className="text-sm text-[#94A3B8]">No webhook events match these filters.</p>
                            <p className="text-xs text-[#64748B] mt-2 italic">When Shopify fires an event, it will appear here within seconds.</p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#243044]" data-testid="wh-events-list">
                            {events.map((ev) => (
                                <li
                                    key={ev.id}
                                    data-testid={`wh-event-${ev.id}`}
                                    onClick={() => openEvent(ev)}
                                    className="px-6 py-4 grid grid-cols-12 gap-4 items-center hover:bg-[#0B1220] transition-colors cursor-pointer"
                                >
                                    <div className="col-span-3">
                                        <div className="text-sm text-white font-medium font-mono">{ev.topic}</div>
                                        <div className="text-[10px] text-[#64748B] mt-0.5 uppercase tracking-[0.18em]">
                                            {ev.hmac_valid ? "hmac ok" : ev.error === "webhook_not_configured" ? "no secret" : "hmac fail"}
                                        </div>
                                    </div>
                                    <div className="col-span-3 min-w-0">
                                        <div className="text-xs text-[#CBD5E1] truncate">{ev.email || <span className="text-[#64748B]">—</span>}</div>
                                        <div className="text-[10px] text-[#64748B] mt-0.5 font-mono truncate">
                                            {ev.customer_id ? `cust ${ev.customer_id}` : "no customer"}
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-xs text-[#94A3B8]">
                                        {formatTime(ev.received_at)}
                                    </div>
                                    <div className="col-span-2">
                                        <StatusPill status={ev.status} />
                                    </div>
                                    <div className="col-span-2 text-right text-[10px] text-[#64748B] font-mono truncate" title={ev.id}>
                                        {ev.id.slice(0, 8)}…
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Detail drawer */}
            {selected && (
                <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/60 backdrop-blur-sm" onClick={() => setSelected(null)}>
                    <div
                        data-testid="wh-event-detail"
                        className="w-full max-w-2xl bg-[#0B1220] border-l border-[#243044] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <header className="px-8 py-6 border-b border-[#243044] sticky top-0 bg-[#0B1220] z-10">
                            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] mb-2">
                                Webhook event
                            </div>
                            <h2 className="text-xl font-bold text-white font-mono">{selected.topic}</h2>
                            <p className="text-xs text-[#64748B] mt-2 font-mono">{selected.id}</p>
                        </header>
                        <div className="px-8 py-6 space-y-6 text-sm">
                            <Field label="Status"><StatusPill status={selected.status} /></Field>
                            <Field label="Received">{formatTime(selected.received_at)}</Field>
                            <Field label="HMAC">{selected.hmac_valid ? <span className="text-[#56CCF2]">Valid</span> : <span className="text-[#FB7185]">Failed / not checked</span>}</Field>
                            <Field label="Shop domain"><span className="font-mono text-[#CBD5E1]">{selected.shop_domain || "—"}</span></Field>
                            <Field label="Shopify webhook id"><span className="font-mono text-[#CBD5E1]">{selected.shopify_webhook_id || "—"}</span></Field>
                            <Field label="Customer"><span className="font-mono text-[#CBD5E1]">{selected.customer_id || "—"}</span></Field>
                            <Field label="Email"><span className="text-[#CBD5E1]">{selected.email || "—"}</span></Field>
                            <Field label="Body size"><span className="text-[#CBD5E1]">{selected.body_size} bytes · hash {selected.body_hash}</span></Field>
                            {selected.error && (
                                <Field label="Error"><span className="text-[#FB7185] font-mono">{selected.error}</span></Field>
                            )}
                            {selected.result && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-2">Handler result</div>
                                    <pre className="text-xs bg-[#111827] border border-[#243044] rounded-lg p-4 overflow-x-auto text-[#94A3B8]">{JSON.stringify(selected.result, null, 2)}</pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

function Field({ label, children }) {
    return (
        <div className="grid grid-cols-3 gap-4 items-baseline">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#64748B]">{label}</div>
            <div className="col-span-2 text-sm">{children}</div>
        </div>
    );
}
